import {inject, Injectable} from '@angular/core';
import {WorkbenchCommand, WorkbenchPlugin, WorkbenchPluginId} from '../types';
import {DevicePlugin} from '../../workbench-plugins/device/device-plugin';
import {TestMgmtPlugin} from '../../workbench-plugins/testmgmt/testmgmt-plugin';
import {concat, Observable, of} from 'rxjs';
import {AuditPlugin} from '../../workbench-plugins/audit/audit-plugin';
import {WorkbenchToolsPlugin} from '../../workbench-plugins/workbench-tools/workbench-tools-plugin';
import {A11yToolsPlugin} from '../../workbench-plugins/a11y-tools/a11y-tools-plugin';
import {ExpeditionPlugin} from '../../workbench-plugins/expedition/expedition-plugin';
import {InspectorPlugin} from '../../workbench-plugins/inspector/inspector-plugin';
import * as _ from 'lodash';
import {ConsolePlugin} from '../../workbench-plugins/console/console-plugin';
import {ScreenshotPlugin} from '../../workbench-plugins/screenshot/screenshot-plugin';
import {MediaBrowserPlugin} from '../../workbench-plugins/media-browser/media-browser-plugin';
import {MailPlugin} from '../../workbench-plugins/mail/mail-plugin';
import {VideoRecordingPlugin} from '../../workbench-plugins/video-recording/video-recording-plugin';
import {QualitySensePlugin} from '../../workbench-plugins/quality-sense/quality-sense-plugin';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import {map, take} from 'rxjs/operators';
import {WORKBENCH_QUALITY_SENSE_FEATURE_ID} from '../quality-sense';

export const SHARED_WORKBENCH_PLUGINS: WorkbenchPluginId[] = [
    'quality-sense',
    'console',
    'device',
    'workbench-tools',
    'screenshot',
    'video-recording',
    'inspector',
    'media-browser',
    'testmgmt',
    'mail',
    'audit',
    'a11y-tools',
];

export interface PluginRegistry {
    registerPlugin(plugin: WorkbenchPlugin): void;
    registerCommand(command: WorkbenchCommand): void;
}

@Injectable()
export class WorkbenchPluginManager implements PluginRegistry {
    private readonly featureService = inject(FeatureService);

    registeredPlugins: {[pluginId in WorkbenchPluginId]?: WorkbenchPlugin} = {};
    availablePlugins: {[pluginId in WorkbenchPluginId]?: WorkbenchPlugin} = {
        'console': inject(ConsolePlugin),
        'device': inject(DevicePlugin),
        'testmgmt': inject(TestMgmtPlugin),
        'audit': inject(AuditPlugin),
        'expedition': inject(ExpeditionPlugin),
        'a11y-tools': inject(A11yToolsPlugin),
        'workbench-tools': inject(WorkbenchToolsPlugin),
        'inspector': inject(InspectorPlugin),
        'screenshot': inject(ScreenshotPlugin),
        'video-recording': inject(VideoRecordingPlugin),
        'media-browser': inject(MediaBrowserPlugin),
        'mail': inject(MailPlugin),
        'quality-sense': inject(QualitySensePlugin),
    };

    registerPlugin(plugin: WorkbenchPlugin) {
        if (this.registeredPlugins[plugin.id]) {
            console.warn(`Plugin with id ${plugin.id} already registered. Skipping.`);
        }

        this.registeredPlugins[plugin.id] = plugin;
    }

    registerCommand(command: WorkbenchCommand): void {
    }

    registerPlugins(plugins: WorkbenchPluginId[] = SHARED_WORKBENCH_PLUGINS): Observable<void> {
        if (!plugins.includes('quality-sense')) {
            this.registerAvailablePlugins(plugins);
            return of(undefined);
        }

        return this.featureService.resolveFeature(WORKBENCH_QUALITY_SENSE_FEATURE_ID).pipe(
            take(1),
            map(feature => {
                this.registerAvailablePlugins(
                    feature?.isActive === true ? plugins : plugins.filter(pluginId => pluginId !== 'quality-sense')
                );
            })
        );
    }

    private registerAvailablePlugins(plugins: WorkbenchPluginId[]): void {
        plugins.forEach(pluginId => {
            const plugin = this.availablePlugins[pluginId];
            if (!plugin) {
                throw new Error(`Plugin with id ${pluginId} not available.`);
            }
            this.registerPlugin(plugin);
        });
    }

    /**
     * Callback for when the workbench is initialized.
     * When this happens, the plugins can be initialized, i.e. they can register their commands, view containers, views, etc.
     */
    onWorkbenchInit(): Observable<void> {
        // No shared plugins registered.
        if(_.isEmpty(this.registeredPlugins)){
            return of(undefined);
        }

        return concat(Object.values(this.registeredPlugins).map(pluginInfo => {
            const plugin = this.availablePlugins[pluginInfo.id];
            plugin.registerViews();
            plugin.onWorkbenchInit();
        }));
    }

    isPluginRegistered(pluginId: WorkbenchPluginId): boolean {
        return !!this.registeredPlugins[pluginId];
    }
}
